import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Link as LinkIcon, Send } from "lucide-react";
import { API } from "@/contexts/AuthContext";

const FOHSendYestimate = () => {
  const { yestimateId } = useParams();
  const [yestimate, setYestimate] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState("email");
  const [customerEmail, setCustomerEmail] = useState("");
  const [approvalLink, setApprovalLink] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/foh/yestimate/${yestimateId}`)
      .then(res => {
        setYestimate(res.data.yestimate);
        setJob(res.data.job);
      })
      .finally(() => setLoading(false));
  }, [yestimateId]);

  const handleGenerateApprovalLink = async () => {
    setGeneratingLink(true);
    try {
      const res = await axios.post(`${API}/foh/yestimate/${yestimateId}/approval-token`);
      setApprovalLink(res.data.approval_url);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to generate link");
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(approvalLink);
    alert("Link copied to clipboard!");
  };

  const handleSend = async () => {
    if (deliveryMethod === "email" && !customerEmail) {
      alert("Please enter customer email");
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${API}/foh/yestimate/${yestimateId}/send`, {
        delivery_method: deliveryMethod,
        customer_email: deliveryMethod === "email" ? customerEmail : null
      });
      navigate(`/app/foh/yestimate/${yestimateId}/audit`);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b px-6 py-4">
        <div className="max-w-2xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/app/foh/yestimate/${yestimateId}/preview`)} className="mb-2">
            ← Back to Preview
          </Button>
          <h1 className="font-bold text-xl text-slate-800">Send Yestimate</h1>
          <p className="text-slate-500">{job?.reg} • {job?.make} {job?.model}</p>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Delivery Method</CardTitle>
            <CardDescription>Choose how to send the Yestimate to the customer</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <button
                className={`p-4 border rounded-lg text-left transition-all ${
                  deliveryMethod === "email" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"
                }`}
                onClick={() => setDeliveryMethod("email")}
                data-testid="delivery-email-btn"
              >
                <Mail className="w-5 h-5 text-slate-600 mb-2" />
                <p className="font-medium">Email</p>
                <p className="text-sm text-slate-500">Send directly to customer inbox</p>
              </button>
              <button
                className={`p-4 border rounded-lg text-left transition-all ${
                  deliveryMethod === "link" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"
                }`}
                onClick={() => setDeliveryMethod("link")}
                data-testid="delivery-link-btn"
              >
                <LinkIcon className="w-5 h-5 text-slate-600 mb-2" />
                <p className="font-medium">Secure Link</p>
                <p className="text-sm text-slate-500">Generate link to share manually</p>
              </button>
            </div>

            {deliveryMethod === "email" && (
              <div>
                <Label>Customer Email</Label>
                <Input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="customer@example.com"
                  data-testid="customer-email-input"
                />
              </div>
            )}

            {deliveryMethod === "link" && (
              <div className="space-y-3">
                {!approvalLink ? (
                  <Button 
                    onClick={handleGenerateApprovalLink}
                    disabled={generatingLink}
                    className="w-full"
                  >
                    {generatingLink ? "Generating..." : "Generate Customer Approval Link"}
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Label>Customer Approval Link</Label>
                    <div className="flex gap-2">
                      <Input value={approvalLink} readOnly className="bg-slate-50" />
                      <Button variant="outline" onClick={copyToClipboard}>Copy</Button>
                    </div>
                    <p className="text-xs text-green-600">✓ Link generated! Share this with the customer.</p>
                  </div>
                )}
              </div>
            )}

            <div className="bg-slate-100 rounded p-4">
              <p className="text-sm text-slate-600">
                <strong>Note:</strong> Once sent, this Yestimate will be locked. Any changes will require creating a new version.
              </p>
            </div>

            <Button 
              className="w-full" 
              onClick={handleSend}
              disabled={sending || (deliveryMethod === "email" && !customerEmail)}
              data-testid="send-yestimate-btn"
            >
              {sending ? "Sending..." : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Yestimate
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FOHSendYestimate;
